<template>
    <div
        class="flex flex-col rounded-xl border-solid border @container"
        :class="selected ? 'border-sky-200 bg-sky-50 ' : 'border-slate-100 bg-white'"
    >
        <div class="flex flex-col items-stretch justify-between gap-1 p-0.5 @xl:flex-row @xl:items-center @xl:pr-0">
            <div
                class="group/checkbox flex grow cursor-pointer items-start gap-2 rounded-l-lg rounded-r-sm py-4 pl-3 @xl:pl-4 pr-3 transition @xl:items-center"
                :class="selected ? 'hover:bg-sky-100/50' : 'hover:bg-sky-50/50'"
                role="button"
                tabindex="0"
                :data-test="`toggle-resource-${resource.resource_type}-${resource.resource_id}`"
                @click.prevent="emit('selected')"
                @keydown.space.prevent="emit('selected')"
                @keydown.enter.prevent="emit('selected')"
            >
                <div
                    class="relative mt-0.5 transition duration-150 group-hover/checkbox:scale-110 group-hover/checkbox:duration-300 group-hover/checkbox:ease-[cubic-bezier(0.5,4,0.4,1)] group-active/checkbox:scale-95 group-active/checkbox:duration-150 group-active/checkbox:ease-in-out @xl:mt-0"
                >
                    <div
                        class="flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-solid border-slate-400"
                    ></div>

                    <div
                        class="absolute inset-bs-0 z-0 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-solid border-sky-500 bg-sky-300 transition"
                        :class="selected ? '' : 'border-slate-400 bg-white '"
                    >
                        <font-awesome-icon
                            widthAuto
                            data-test="selected-icon"
                            :icon="faCheck"
                            class="!size-2.5 transition"
                            :class="selected ? '' : 'opacity-0'"
                        ></font-awesome-icon>
                    </div>
                </div>

                <div
                    class="grow origin-left text-sm leading-tight transition duration-150 ease-in-out break-all group-active/checkbox:-translate-x-[1.5px]"
                >
                    <highlighter
                        :searchWords="props.search ? [props.search] : []"
                        :autoEscape="true"
                        highlightClassName="text-amber-600 p-0 bg-transparent"
                        :textToHighlight="props.resource.resource_name || resourceName(props.resource) || 'Loading...'"
                    >
                    </highlighter>
                </div>
                <font-awesome-icon
                    widthAuto
                    :icon="resourceTypeIcon(props.resource.resource_type, 'regular')"
                ></font-awesome-icon>
            </div>

            <div class="flex items-center gap-3 px-3 pb-3 @xl:pb-0">
                <vx-button
                    size="sm"
                    :color="selected ? 'muted' : 'muted-light'"
                    :outline="!isOpen"
                    class="w-full mix-blend-multiply"
                    data-test="details-button"
                    @click.stop="toggleDetails"
                >
                    Details
                    <font-awesome-icon
                        widthAuto
                        :icon="faCaretDown"
                        class="fa-solid fa-caret-down transition"
                        :class="isOpen && ' rotate-180'"
                    ></font-awesome-icon>
                </vx-button>
            </div>
        </div>

        <animate-height :height="isOpen ? 'auto' : 0" content-class="block!" :duration="isOpen ? 300 : 150">
            <div
                :class="selected ? 'border-t-sky-200' : 'border-t-slate-100'"
                class="grid grid-cols-1 gap-4 border-t border-solid border-x-0 border-b-0 p-5 @xl:grid-cols-2"
            >
                <div class="flex flex-col gap-1.5">
                    <analytics-select-resource-item-detail>
                        <template v-slot:label>Resource Type</template>
                        <div class="flex items-center">
                            <font-awesome-icon
                                widthAuto
                                :icon="resourceTypeIcon(props.resource.resource_type)"
                                class="text-slate-400 mr-1"
                            ></font-awesome-icon>
                            <span>{{ resourceTypeLabel(props.resource.resource_type) }}</span>
                        </div>
                    </analytics-select-resource-item-detail>

                    <analytics-select-resource-item-detail v-if="resourceDetail?.audience_description">
                        <template v-slot:label>Audience</template>
                        {{ resourceDetail.audience_description }}
                    </analytics-select-resource-item-detail>

                    <analytics-select-resource-item-detail v-if="resourceDetail?.launched_at">
                        <template v-slot:label>Launched</template>
                        {{ dateTime(resourceDetail.launched_at) }}
                    </analytics-select-resource-item-detail>
                </div>
                <div>
                    <message-cloud
                        v-if="resourceDetail?.main_message"
                        class="relative"
                        fullWidth
                        :showDetails="false"
                        :message="resourceDetail?.main_message"
                    ></message-cloud>
                </div>
            </div>
        </animate-height>
    </div>
</template>
<script setup>
import Highlighter from 'vue-highlight-words';
import { computed, ref } from 'vue';
import { VxButton } from '@voxie/frontend-components';
import AnimateHeight from 'vue-animate-height';
import { resourceTypeIcon, resourceTypeLabel } from '~/utils/analytics';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCaretDown, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useResourcesDetails } from '~/components/analytics/useResourcesDetails';
import AnalyticsSelectResourceItemDetail from '~/components/analytics/partial/select-resources/AnalyticsSelectResourceItemDetail.vue';
import { dateTime } from '~/utils/helpers';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';
import { useAnalyticsSelectResources } from '~/components/analytics/partial/select-resources/useAnalyticsSelectResources';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: false,
    },
    groupId: {
        type: String,
        required: false,
    },
    resource: {
        type: Object,
        required: true,
    },
    search: {
        type: String,
        required: false,
    },
    selected: {
        type: Boolean,
        default: false,
    },
    openDefault: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['selected']);

const isOpen = ref(props.openDefault);

const { getResourceDetail, fetchResourceDetails } = useResourcesDetails();

const { resourceName } = useAnalyticsSelectResources();

const toggleDetails = () => {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        fetchResourceDetails(teamId, [props.resource], props.groupId, props.orgUnitId);
    }
};

const resourceDetail = computed(() => getResourceDetail(props.resource.resource_type, props.resource.resource_id));
</script>
